<template>
  <!-- Authentication Loading Overlay -->
  <div id="app-loading" class="auth-loading">
    <div class="loading-content">
      <div class="spinner"></div>
      <p>Authenticating...</p>
    </div>
  </div>

  <nav v-if="nav" id="adminDesktopNav" class="free-trial" :class="navEnlarge && 'navEnlarge'">
    <div class="nav-top">
      <img v-if="!settings.logo" src="@/assets/logo-wide-light-2.png" alt="logo" id="logo" />
      <img v-else :src="settings.logo" alt="logo" id="logo" />
      <div v-if="staffNav">
        <a href="/">
          <SquareArrowLeft />
          <span>Exit Admin</span>
        </a>
        <router-link to="/admin">
          <LayoutDashboard />
          <span>Tracking</span>
        </router-link>
        <router-link to="/admin/manage-clients">
          <Users />
          <span>Manage Clients</span>
        </router-link>
        <router-link to="/admin/request-log">
          <ClipboardList />
          <span>Request Log</span>
        </router-link>
        <router-link to="/admin/helper-chats">
          <BotMessageSquare />
          <span>Helper Chats</span>
        </router-link>
        <router-link to="/admin/integration-tester">
          <FlaskConical />
          <span>Integration Tester</span>
        </router-link>
      </div>
      <div v-else>
        <router-link to="/">
          <LayoutDashboard />
          <span>Dashboard</span>
        </router-link>
        <router-link to="/calls">
          <Phone />
          <span>Calls</span>
        </router-link>
        <router-link to="/messages">
          <Send />
          <span>Messages</span>
        </router-link>
        <router-link to="/outbound" v-if="admin && showSettings">
          <PhoneOutgoing />
          <span>Outbound</span>
        </router-link>
        <router-link to="/channels" v-if="admin && showSettings">
          <MessagesSquare />
          <span>Channels</span>
        </router-link>
        <router-link to="/clinic" v-if="admin && showSettings">
          <Building />
          <span>Clinic Setup</span>
        </router-link>
        <router-link to="/settings" v-if="admin && showSettings">
          <Settings />
          <span>Settings</span>
        </router-link>
      </div>
    </div>

    <div class="nav-bottom">
      <a v-if="!isv" @click="openHelp" style="cursor: pointer" id="ai-help">
        <MessageCircleMore />
        <span style="font-weight: 500">Get help</span>
      </a>
      <div id="isvToggle" v-if="isv" @click="showClients = !showClients">
        <div class="current-client">
          <div class="current-client-avatar">
            <i class="fa fa-globe" v-if="client === 'all'"></i>
            <span v-else>{{ client?.name?.charAt(0)?.toUpperCase() || 'L' }}</span>
          </div>
          <span class="current-client-name">{{ client == 'all' ? 'All Clients' : (client?.name || 'Loading') }}</span>
        </div>
        <i class="fa fa-chevron-down toggle-icon" :class="{ rotated: showClients }"></i>
      </div>
      <div v-if="!isv" id="bottomTab">
        <div id="userLogin" v-if="name">
          <div class="profileIcon" :class="admin ? 'blue' : 'pink'">
            <span>{{ initials }}</span>
          </div>
          <div class="userName">
            <p class="name" :title="name">{{ name }}</p>
            <p class="admin">{{ admin ? 'Admin' : 'User' }}</p>
          </div>
        </div>
        <div id="notificationContainer" :class="navEnlarge && 'navEnlarge'">
          <i class="fa fa-bell" id="notificationBell" @click="toggleNotifications">
            <span v-if="unreadNotifications" class="notification-counter">{{ unreadNotifications }}</span>
          </i>
        </div>
      </div>
    </div>
  </nav>

  <!-- Move ISV selector outside nav to prevent clipping -->
  <div id="selectISV" v-if="showClients && isv" :class="navEnlarge && 'navEnlarge'">
    <div class="client-selector-header">
      <h4>Select Client Account</h4>
      <button class="close-selector" @click="showClients = false">
        <i class="fa fa-times"></i>
      </button>
    </div>
    <div class="client-list">
      <div class="client-item" :class="{ active: client === 'all' }" @click="setClient('all')">
        <div class="client-avatar all-clients">
          <i class="fa fa-globe"></i>
        </div>
        <div class="client-info">
          <span class="client-name">All Clients</span>
          <span class="client-description">View aggregated data</span>
        </div>
        <div class="client-status">
          <i class="fa fa-check" v-if="client === 'all'"></i>
        </div>
      </div>

      <div class="client-item" v-for="clientAccount of isv.subaccounts" :key="clientAccount._id"
        :class="{ active: client?._id === clientAccount._id }" @click="setClient(clientAccount)">
        <div class="client-avatar">
          <span>{{ clientAccount.name.charAt(0).toUpperCase() }}</span>
        </div>
        <div class="client-info">
          <span class="client-name">{{ clientAccount.name }}</span>
          <span class="client-description">{{ clientAccount.email || 'Individual account' }}</span>
        </div>
        <div class="client-status">
          <i class="fa fa-check" v-if="client?._id === clientAccount._id"></i>
        </div>
      </div>
    </div>

    <div class="client-actions">
      <router-link to="/isv" class="add-client-btn">
        <i class="fa fa-plus"></i>
        <span>Create New Account</span>
      </router-link>
    </div>
  </div>
  <nav v-if="mobileNav && !['login', 'register', 'onboarding'].includes(this.$route.name)" id="adminMobileNav" :class="isv && 'isv'">
    <div id="flex-nav-items">
      <router-link to="/calls">
        <i class="fa fa-phone"></i>
        <span>Calls</span>
      </router-link>
      <router-link to="/messages">
        <i class="fa fa-paper-plane"></i>
        <span>Messages</span>
      </router-link>
      <router-link to="/settings">
        <i class="fa fa-gear"></i>
        <span>Settings</span>
      </router-link>
    </div>
    <div id="selectISV" v-if="showClients">
      <div class="mobile-client-list">
        <div class="mobile-client-item" :class="{ active: client === 'all' }" @click="setClient('all')">
          <div class="mobile-client-avatar all-clients">
            <i class="fa fa-globe"></i>
          </div>
          <span class="mobile-client-name">All Clients</span>
          <i class="fa fa-check mobile-check" v-if="client === 'all'"></i>
        </div>

        <div class="mobile-client-item" v-for="clientAccount of isv.subaccounts" :key="clientAccount._id"
          :class="{ active: client?._id === clientAccount._id }" @click="setClient(clientAccount)">
          <div class="mobile-client-avatar">
            <span>{{ clientAccount.name.charAt(0).toUpperCase() }}</span>
          </div>
          <span class="mobile-client-name">{{ clientAccount.name }}</span>
          <i class="fa fa-check mobile-check" v-if="client?._id === clientAccount._id"></i>
        </div>

        <router-link to="/isv" class="mobile-add-client">
          <i class="fa fa-plus"></i>
          <span>Create New Account</span>
        </router-link>
      </div>
    </div>
    <div id="isvToggle" v-if="isv" @click="showClients = !showClients">
      <div class="mobile-current-client">
        <div class="mobile-current-avatar">
          <i class="fa fa-globe" v-if="client === 'all'"></i>
          <span v-else>{{ client?.name?.charAt(0)?.toUpperCase() || 'L' }}</span>
        </div>
        <span>{{ client == 'all' ? 'All Clients' : (client?.name || 'Loading') }}</span>
      </div>
      <i class="fa fa-chevron-down" :class="{ rotated: showClients }"></i>
    </div>
  </nav>

  <!-- Payment Modal Component -->
  <PaymentModal v-if="!isLapsed" :hasPaid="hasPaid" :freeTrial="freeTrial" :mobile="mobile" :routeName="this.$route.name" />

  <!-- Resubscribe Modal (for lapsed/cancelled subscribers) -->
  <ResubscribeModal :isLapsed="isLapsed" :mobile="mobile" :routeName="this.$route.name" />

  <!-- Getting Started Checklist (shown while not yet paid) -->
  <GettingStarted v-if="!hasPaid && !isv && !gettingStartedDismissed && !publicRoute" :freeTrial="freeTrial" @dismiss="gettingStartedDismissed = true" />

  <router-view class="router-view" :class="navEnlarge && 'navEnlarge', isv && 'isv'" :website="website"
    :showMobileNav="showMobileNav" :hideMobileNav="hideMobileNav" />

  <div v-if="showNotifications" class="notifications-popup" :class="navEnlarge && 'navEnlarge'">
    <div class="notifications-header">
      <h3>Notifications</h3>
      <button @click="markAllAsRead" class="mark-all-read">Mark all as read</button>
    </div>
    <div class="notifications-list" v-if="notifications.length > 0">
      <div v-for="notification in notifications" :key="notification._id" class="notification-item"
        :class="{ 'unread': !notification.read }" @click="readNotification(notification)">
        <div class="notification-icon">
          <i class="fa fa-info-circle" v-if="notification.type === 'info'"></i>
          <i class="fa fa-check-circle" v-else-if="notification.type === 'success'"></i>
          <i class="fa fa-exclamation-circle" v-else-if="notification.type === 'warning'"></i>
          <i class="fa fa-times-circle" v-else-if="notification.type === 'error'"></i>
        </div>
        <div class="notification-content">
          <h4>{{ notification.title }}</h4>
          <p>{{ notification.message }}</p>
          <small>{{ formatDate(notification.date) }}</small>
        </div>
      </div>
    </div>
    <div class="notifications-empty" v-else>
      <p>No notifications to display</p>
    </div>
  </div>

  <!-- Promo / outage popup -->
  <PopUpPromo v-if="!publicRoute && authed" />

  <!-- Help Modal -->
  <div v-if="showHelpModal" class="help-modal-overlay" @click.self="closeHelpModal">
    <div class="help-modal">
      <div class="help-modal-content">
        <div class="help-section">
          <AIHelper />
        </div>
        <div class="help-divider"></div>
        <div class="help-section help-center-section">
          <button @click="closeHelpModal" class="close-modal-btn">
            <i class="fa fa-times"></i>
          </button>
          <div class="help-center-content">
            <i class="fa fa-book"></i>
            <div>
              <h3>Help Center</h3>
              <p>Browse guides, tutorials, and documentation</p>
            </div>
          </div>
          <button @click="openHelpCenter" class="help-center-btn">
            Visit Help Center
            <i class="fa fa-external-link"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  LayoutDashboard,
  Send,
  Phone,
  PhoneOutgoing,
  MessagesSquare,
  Building,
  Settings,
  MessageCircleMore,
  SquareArrowLeft,
  Users,
  BotMessageSquare,
  FlaskConical,
  ClipboardList,
} from 'lucide-vue-next';
import {
  getClientSettings,
  getISV,
  createPayment,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  switchAccount,
  logout,
  activateToken,
} from '@/app/admin-routes';
import authService from '@/utils/auth.js';
import { startProductTour } from '@/utils/productTour.js';
import PaymentModal from '@/components/PaymentModal.vue';
import ResubscribeModal from '@/components/ResubscribeModal.vue';
import GettingStarted from '@/components/GettingStarted.vue';
import AIHelper from '@/components/AIHelper.vue';
import PopUpPromo from '@/components/PopUpPromo.vue';

export default {
  name: 'App',
  components: {
    LayoutDashboard,
    Send,
    Phone,
    PhoneOutgoing,
    MessagesSquare,
    Building,
    Settings,
    MessageCircleMore,
    SquareArrowLeft,
    Users,
    BotMessageSquare,
    FlaskConical,
    ClipboardList,
    PaymentModal,
    ResubscribeModal,
    GettingStarted,
    AIHelper,
    PopUpPromo,
  },
  data() {
    return {
      isv: null,
      client: null,
      authed: false,
      staffNav: false,
      admin: false, // Default to false, will be set by server validation
      nav: true,
      clientId: localStorage.client_id,
      email: localStorage.email,
      name: '',
      initials: '',
      mobile: window.innerWidth < 600,
      mobileNav: false,
      showNav: false,
      navEnlarge: localStorage.navEnlarge == 'true' ? true : false,
      route: this.$route.name,
      settings: {
        integration: 'cliniko',
        name: '',
      },
      showInfo: false,
      hasPaid: true,
      freeTrial: 0,
      isLapsed: false,
      showClients: false,
      showSettings: localStorage.client_id != 'all' ? true : false,
      notifications: [],
      unreadNotifications: 0,
      showNotifications: false,
      showHelpModal: false,
      gettingStartedDismissed: false,
    };
  },
  computed: {
    publicRoute() {
      return ['login', 'register', 'password', 'connect-stripe', 'audio-player', 'onboarding'].includes(this.$route.name);
    },
  },
  methods: {
    // Validate user permissions server-side to prevent client-side tampering
    async validateUserPermissions() {
      try {
        // Only validate if we have a token
        if (!authService.hasToken()) {
          this.admin = false;
          return;
        }

        const authResult = await authService.validateAuth();

        if (authResult.isValid && authResult.user) {
          // Set admin status based on server response, not localStorage
          this.admin = authResult.user.isAdmin || authResult.user.security_level >= 5;

          // Update localStorage with validated data (for consistency)
          localStorage.setItem('isAdmin', this.admin.toString());
        } else {
          // Invalid token or auth failed
          this.admin = false;
          localStorage.removeItem('isAdmin');
        }
      } catch (error) {
        console.error('Permission validation error:', error);
        this.admin = false;
        localStorage.removeItem('isAdmin');
      }
    },

    async getSettings() {
      try {
        const settings = await getClientSettings();
        if (!settings) {
          localStorage.removeItem('settings');
          return;
        }
        document.documentElement.style.setProperty('--colourP', '#2596be');
        document.documentElement.style.setProperty('--colourS', '#2562be');
        document.documentElement.style.setProperty('--text', '#2c3e50');
        localStorage.setItem('settings', JSON.stringify(settings));
        this.settings = settings;
        // Apply the settings
        if (!settings.hasPaid && !localStorage.isv_id) {
          if (settings.isLapsed) {
            this.hasPaid = false;
            this.isLapsed = true;
          } else if (localStorage.registrationDate) {
            const registrationDate = new Date(localStorage.registrationDate);
            let trialPeriod = 14;
            if (new Date(localStorage.registrationDate).getTime() > new Date('2026-04-05').getTime()) {
              trialPeriod = 7;
            }
            const trialExtensionDays = Number(settings.trialExtensionDays) || 0;
            const daysRemaining = trialPeriod + trialExtensionDays - Math.floor((new Date() - registrationDate) / (1000 * 60 * 60 * 24));
            this.hasPaid = false;
            this.freeTrial = daysRemaining; // Keep negative values to track days expired
          }
        }
        if (settings.hasOutbound == true) {
          localStorage.setItem('hasOutbound', true);
        } else {
          localStorage.removeItem('hasOutbound');
        }

        if (localStorage.isv_id == '692d75414419d016536f4635') {
          this.settings = {
            ...this.settings,
            logo: 'https://dashboard.bookedsolid.co.uk/whitelabels/autopilot.png',
          }
        }
      } catch (error) {
        console.warn('Failed to load settings:', error);
        // Don't redirect here, let the router guard handle it
      }
    },
    setSize() {
      if (window.innerWidth < 600) {
        this.nav = false;
        this.mobileNav = true;
      } else {
        this.nav = true;
        this.mobileNav = false;
      }
    },
    toggleNavEnlarge() {
      if (this.navEnlarge) {
        this.navEnlarge = false;
        localStorage.navEnlarge = 'false';
      } else {
        this.navEnlarge = true;
        localStorage.navEnlarge = 'true';
      }
      this.showInfo = false;
      this.showNotifications = false; // Close notifications when toggling nav
    },
    showMobileNav() {
      this.mobileNav = true;
    },
    hideMobileNav() {
      this.mobileNav = false;
    },
    async setClient(client) {
      if (client == 'all') {
        localStorage.client_id = 'all';
        await switchAccount('all');
        window.location.href = '/';
        this.client = 'all';
      } else {
        localStorage.client_id = client._id;
        await switchAccount(client._id);
        window.location.href = '/';
        this.client = client;
      }
    },
    subscribe() {
      createPayment();
    },
    openHelp() {
      this.showHelpModal = true;
    },
    closeHelpModal() {
      this.showHelpModal = false;
    },
    openHelpCenter() {
      window.open('https://help.bookedsolid.health/', '_blank');
    },

    // New notification methods
    async fetchNotifications() {
      try {
        const response = await getNotifications();
        console.log('Notifications:', response);
        if (response && response.success) {
          this.notifications = response.notifications;
          this.unreadNotifications = this.notifications.filter(n => !n.read).length;
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    },

    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
      if (this.showNotifications) {
        this.fetchNotifications();
      }
    },

    async readNotification(notification) {
      if (!notification.read) {
        await markNotificationRead(notification._id);
        notification.read = true;
        this.unreadNotifications--;
      }

      // Handle any specific action based on notification type
      if (notification.action && notification.action.type === 'navigate') {
        this.$router.push(notification.action.path);
        this.showNotifications = false;
      }
    },

    async markAllAsRead() {
      await markAllNotificationsRead();
      this.notifications.forEach(notification => {
        notification.read = true;
      });
      this.unreadNotifications = 0;
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;

      // Less than a day
      if (diff < 86400000) {
        // Less than an hour
        if (diff < 3600000) {
          const minutes = Math.floor(diff / 60000);
          return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
        } else {
          const hours = Math.floor(diff / 3600000);
          return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
        }
      } else if (diff < 172800000) {
        // Less than 2 days
        return 'Yesterday';
      } else {
        // Format date
        return date.toLocaleDateString();
      }
    }
  },
  async mounted() {
    // Redirect to dashboard.bookedsolid.health if the user is on the old dashboard domain (dashboard.bookedsolid.co.uk)
    if (window.location.hostname === 'dashboard.bookedsolid.co.uk') {
      window.location.href = 'https://dashboard.bookedsolid.health' + window.location.pathname + window.location.search;
      return; // Stop further execution
    }

    const publicRoutes = ['login', 'reset_user_password', 'connect-stripe', 'audio-player'];
    const routeName = window.location.pathname.split('/')[1];

    // Handle loginAsClient URL flow FIRST — before any auth validation.
    // The cookie isn't set yet so validateUserPermissions() would redirect to login
    // if we let it run before activating the token.
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    const urlClientId = urlParams.get('client_id');

    if (urlToken && urlClientId) {
      try {
        await activateToken(urlToken, urlClientId);
        localStorage.setItem('client_id', urlClientId);
        window.history.replaceState({}, document.title, window.location.pathname);
        window.location.reload();
      } catch (e) {
        console.error('Failed to activate token from URL params:', e);
        // Do NOT strip the URL params or reload — keep them so the user can retry by refreshing.
      }
      return;
    }

    if (localStorage.isv_id && !publicRoutes.includes(routeName)) {
      try {
        this.isv = await getISV();
        console.log(this.isv);
        localStorage.setItem('navEnlarge', 'true');
        if (localStorage.client_id == 'all') {
          this.client = 'all';
        } else {
          this.client = this.isv.subaccounts.find((client) => client._id == localStorage.client_id);
        }
      } catch (error) {
        console.warn('Failed to load ISV data:', error);
        // Don't redirect here, let the router guard handle it
      }
    }

    // Only validate admin status if not on login or password reset pages to avoid redirect loops
    if (!publicRoutes.includes(routeName)) {
      await this.validateUserPermissions();
    }

    if (routeName.includes('admin')) {
      this.staffNav = true;
    }

    const userName = localStorage.name || '';
    if (userName != 'undefined') {
      this.name = userName;
      this.initials = userName.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    document.getElementsByTagName('body')[0].classList.add('admin');
    if (localStorage.getItem('client_id')) this.authed = true;
    this.setSize();
    window.addEventListener('resize', this.setSize);

    // Only get settings if not on login or password reset pages to avoid API calls that could trigger redirects
    if (!publicRoutes.includes(routeName)) {
      this.getSettings();
    }
    document.addEventListener('settings_updated', this.getSettings);

    // Listen for auth updates to revalidate permissions
    window.addEventListener('auth_updated', this.validateUserPermissions);
    if (!localStorage.getItem('navEnlarge')) {
      localStorage.navEnlarge = 'true';
      this.navEnlarge = true;
    }

    // Fetch notifications on mount (only for authenticated pages)
    if (!publicRoutes.includes(routeName)) {
      this.fetchNotifications();
    }

    // Start product tour after onboarding completion
    if (localStorage.getItem('startProductTour') === 'true' && !publicRoutes.includes(routeName)) {
      localStorage.removeItem('startProductTour');
      // Small delay to let the app fully render before starting the tour
      setTimeout(() => {
        startProductTour(this.$router);
      }, 800);
    }

    // Add click outside to close notifications
    document.addEventListener('click', (e) => {
      if (this.showNotifications && !e.target.closest('#notificationContainer')) {
        this.showNotifications = false;
      }

      // Close ISV client selector when clicking outside
      if (this.showClients && !e.target.closest('#selectISV') && !e.target.closest('#isvToggle')) {
        this.showClients = false;
      }
    });

    // Check if a subscription payment was requested
    if (urlParams.get('subscribe') === 'true') {
      this.subscribe();
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.setSize);
    window.removeEventListener('auth_updated', this.validateUserPermissions);

    // Clear notification interval
    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
    }

    // Remove click outside listener
    document.removeEventListener('click', () => { });
  },
}
</script>

<style lang="scss">
@import "/src/assets/styles/variables.scss";

// ─── Shepherd.js product tour overrides ────────────────────────────────────
.bs-tour-step {
  &.shepherd-has-cancel-icon .shepherd-cancel-icon {
    color: #6b7280;
    &:hover { color: #111; }
  }

  .shepherd-content {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    font-family: 'Plus Jakarta Sans', sans-serif;
    overflow: hidden;
  }

  .shepherd-header {
    background: linear-gradient(135deg, $colourP 0%, $colourS 100%) !important;
    padding: 16px 20px;
    border-bottom: none;

    .shepherd-title {
      color: white;
      font-size: 15px;
      font-weight: 600;
    }

    .shepherd-cancel-icon {
      color: rgba(255,255,255,0.7);
      &:hover { color: white; background: none; }
    }
  }

  .shepherd-text {
    padding: 16px 20px;
    font-size: 14px;
    color: #374151;
    line-height: 1.6;
  }

  .shepherd-footer {
    padding: 12px 20px;
    border-top: 1px solid #f3f4f6;
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    .shepherd-button {
      border-radius: 8px;
      font-size: 13px;
      font-weight: 500;
      padding: 8px 16px;
      cursor: pointer;
      transition: all 0.15s ease;

      &:not(.shepherd-button-secondary) {
        background: linear-gradient(135deg, $colourP 0%, $colourS 100%);
        color: white;
        border: none;
        &:hover { opacity: 0.9; }
      }

      &.shepherd-button-secondary {
        background: transparent;
        color: #6b7280;
        border: 1px solid #e5e7eb;
        &:hover { background: #f5f7fb; color: #374151; }
      }
    }
  }
}

.shepherd-modal-overlay-container {
  fill: rgba(0,0,0,0.45);
}
// ───────────────────────────────────────────────────────────────────────────

body.main {
  margin: 0;
  padding: 0;
}

.v-toast {
  font-family: 'Poppins', sans-serif;
  font-size: small;
}

.admin {

  .router-view,
  #app {
    position: fixed;
    top: 0;
    right: 0;
    width: calc(100% - 80px);
    height: 100%;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: small;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #333;
    transition: width 0.2s;
    background-color: #f5f7fb;

    &.navEnlarge {
      width: calc(100% - 230px);
    }

    h1 {
      color: $colourP;
    }

    button,
    select,
    option,
    input {
      font-family: inherit;
      font-size: inherit;
    }
  }

  #header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 20px;

    #left {
      margin: 10px;
      font-weight: normal;
      font-size: 1.8em;
      color: #333;
    }

    #right {
      display: flex;
      align-items: center;

      select {
        background: none;
        color: #333;
        padding: 10px;
        border: solid 1px #e6e6e6;
        width: 20vw;
        max-width: 200px;
        border-radius: 5px;
        height: 42px;
        box-sizing: border-box;
        margin-left: 20px;
        background-color: white;
      }

      #weekSelect {
        width: 250px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border: solid 1px #e6e6e6;
        background-color: white;
        border-radius: 5px;
      }

      p {
        margin: 0;
        color: #333;
      }

      a {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 10px 15px;
        box-sizing: border-box;
        height: 40px;
        transition: all 0.2s ease-in-out;
        color: #333;

        &:hover {
          cursor: pointer;
          background: #e0e0e0;
        }

        &:first-child {
          border-right: solid 1px #e6e6e6;
        }

        &:last-child {
          border-left: solid 1px #e6e6e6;
        }
      }

      b {
        box-sizing: border-box;
        padding: 10px;
        height: 40px;
        margin-left: 10px;
        color: #333;
        display: flex;
        font-size: 1.1em;
        justify-content: center;
        align-items: center;
      }

      button {
        background: none;
        padding: 10px;
        border: solid 1px #e6e6e6;
        width: 100px;
        text-align: center;
        cursor: pointer;
        transition: all 0.2s ease-in-out;

        &:first-child {
          border-radius: 5px 0 0 5px;
          border-right: none;
        }

        &:last-child {
          border-radius: 0 5px 5px 0;
        }

        &.selected {
          background: linear-gradient(140deg, $colourP, $colourS);
          color: white;
        }

        &:hover {
          background-color: $colourP;
          color: white;
        }
      }
    }
  }

  #adminDesktopNav {
    position: fixed;
    top: 0;
    left: 0;
    width: 50px;
    height: calc(100% - 20px);
    padding: 10px 15px;
    overflow-y: auto;
    overflow-x: hidden;
    background-color: #04182d;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    transition: width 0.2s ease-in-out;

    #logo {
      width: 80%;
      max-height: 50px;
      margin: 12px 0;
      object-fit: contain;
    }

    a {
      font-weight: bold;
      text-decoration: none;
      color: #ccc;
      display: block;
      width: 50px;
      height: 50px;
      margin: 5px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 8px;
      transition: background-color 0.1s ease-in-out, width 0.2s ease-in-out;
      font-size: 20px;

      &:hover {
        background-color: #cccccc30;
      }

      &.router-link-exact-active {
        background-color: lighten(#04182d, 7%);
        color: white;

        svg {
          color: white !important;
        }
      }

      img {
        width: 30px;
      }

      span {
        visibility: hidden;
        width: 0;
        opacity: 0;
        transition: visibility 0s, opacity 0.5s linear;
      }
    }

    #ai-help {
      position: relative;
      height: 40px;
      margin-bottom: 25px;
      border-radius: 12px;
      border: 1px solid transparent;
      background: linear-gradient(#04182d, #04182d) padding-box,
        linear-gradient(120deg, #6ee7ff, #2562be, #a855f7, #6ee7ff) border-box;
      background-size: 200% 200%;
      box-shadow: 0 0 5px rgba(37, 150, 190, 0.35), 0 0 20px rgba(168, 85, 247, 0.25);
      overflow: hidden;
    }

    #ai-help::after {
      content: '';
      position: absolute;
      inset: -3px;
      border-radius: 14px;
      background: linear-gradient(120deg, #6ee7ff, #2562be, #a855f7, #6ee7ff);
      background-size: 200% 200%;
      filter: blur(16px);
      opacity: 0.6;
      z-index: -1;
      animation: ai-border 5s linear infinite;
    }

    #openNav,
    #openInfo {
      position: absolute;
      bottom: 10px;
      left: 20px;
      padding: 15px;
      font-size: medium;
      border-radius: 40px;
      width: 46px;
      box-sizing: border-box;
      color: #85888b;
      transition: all 0.2s;
      cursor: pointer;

      &:hover {
        color: white;
        background-color: lighten(#2c3e50, 10%);
      }
    }

    #openInfo {
      right: 20px;
      left: unset;
      display: none;
    }

    #selectISV {
      position: absolute;
      bottom: 70px;
      left: 8px;
      width: 350px;
      background: white;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
      border: 1px solid rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(10px);
      overflow: hidden;
      z-index: 1000;

      .client-selector-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        background: linear-gradient(135deg, #04182d 0%, lighten(#04182d, 8%) 100%);
        color: white;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);

        h4 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.5px;
        }

        .close-selector {
          background: none;
          border: none;
          color: #ccc;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s ease;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;

          &:hover {
            background: rgba(255, 255, 255, 0.1);
            color: white;
          }
        }
      }

      .client-list {
        max-height: 280px;
        overflow-y: auto;
        padding: 8px 0;

        .client-item {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          cursor: pointer;
          transition: all 0.2s ease;
          border-left: 3px solid transparent;

          &:hover {
            background: #f5f7fb;
            border-left-color: lighten($colourP, 20%);
          }

          &.active {
            background: linear-gradient(90deg, rgba($colourP, 0.1) 0%, transparent 100%);
            border-left-color: $colourP;

            .client-name {
              color: $colourP;
              font-weight: 600;
            }
          }

          .client-avatar {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background: linear-gradient(135deg, lighten($colourP, 10%) 0%, $colourS 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 600;
            font-size: 14px;
            margin-right: 12px;
            flex-shrink: 0;
            box-shadow: 0 2px 8px rgba($colourP, 0.2);

            &.all-clients {
              background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
            }
          }

          .client-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            min-width: 0;

            .client-name {
              font-size: 14px;
              font-weight: 500;
              color: #1f2937;
              margin-bottom: 2px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              width: 100%;
            }

            .client-description {
              font-size: 12px;
              color: #6b7280;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              width: 100%;
            }
          }

          .client-status {
            width: 20px;
            display: flex;
            justify-content: center;
            align-items: center;

            i {
              color: $colourP;
              font-size: 14px;
            }
          }
        }
      }

      .client-actions {
        padding: 12px 16px;
        background: #f5f7fb;
        border-top: 1px solid #e5e7eb;

        .add-client-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 10px 16px;
          background: linear-gradient(135deg, $colourP 0%, $colourS 100%);
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba($colourP, 0.2);

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba($colourP, 0.3);
          }

          i {
            margin-right: 8px;
            font-size: 12px;
          }
        }
      }
    }

    #isvToggle {
      position: absolute;
      bottom: 15px;
      left: 15px;
      right: 15px;
      padding: 12px 16px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #CCCCCC;
      transition: all 0.2s ease;
      cursor: pointer;

      &:hover {
        background: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.2);
      }

      .current-client {
        display: flex;
        align-items: center;
        flex: 1;
        min-width: 0;

        .current-client-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, lighten($colourP, 10%) 0%, $colourS 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 12px;
          margin-right: 10px;
          flex-shrink: 0;
        }

        .current-client-name {
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .toggle-icon {
        font-size: 12px;
        transition: transform 0.3s ease;
        flex-shrink: 0;

        &.rotated {
          transform: rotate(180deg);
        }
      }
    }

    &.navEnlarge {
      width: 200px;

      a {
        width: 200px;
        text-align: left;
        justify-content: flex-start;
        padding-left: 5px;

        svg {
          width: 35px;
          height: 43%;
          display: flex;
          justify-content: center;
          align-items: center;
          color: #bbb;
          font-size: 0.88em;
        }

        &.router-link-exact-active {
          color: white !important;
        }

        img {
          margin: 0 6px;
        }

        span {
          visibility: visible;
          width: auto;
          opacity: 1;
          font-weight: normal;
          font-size: small;
          margin-left: 10px;
          white-space: nowrap;
        }
      }

      #openInfo {
        bottom: 10px;
        display: block;
      }
    }

    #logout {
      cursor: pointer;
    }

    #bottomTab {
      display: flex;
      justify-content: space-between;
      align-items: center;
      max-width: 200px;
      margin: 0 auto 10px;

      #userLogin {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0; // Allow flex item to shrink
        flex: 1; // Take available space but allow shrinking

        .profileIcon {
          width: 35px;
          height: 35px;
          border-radius: 50%;
          background: linear-gradient(135deg, #21c0eb 0%, #2562be 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 14px;
          text-transform: uppercase;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

          &.pink {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }
        }

        .userName {
          display: none; // Hidden by default in collapsed nav
          flex-direction: column;
          align-items: flex-start;
          min-width: 0; // Allow flex item to shrink
          max-width: 120px; // Limit width to prevent overflow
          overflow: hidden;

          .name {
            margin: 0;
            color: #fff;
            font-size: 14px;
            font-weight: 600;
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
          }

          .admin {
            margin: 0;
            color: #bbb;
            font-size: 12px;
            font-weight: 400;
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    &.navEnlarge {
      width: 200px;

      a {
        width: 200px;
        text-align: left;
        justify-content: flex-start;
        padding-left: 5px;
        margin-bottom: 10px;

        svg {
          width: 35px;
          height: 43%;
          display: flex;
          justify-content: center;
          align-items: center;
          color: #bbb;
          font-size: 0.88em;
        }

        &.router-link-exact-active {
          color: white !important;
        }

        img {
          margin: 0 6px;
        }

        span {
          visibility: visible;
          width: auto;
          opacity: 1;
          font-weight: normal;
          font-size: small;
          margin-left: 10px;
          white-space: nowrap;
        }
      }

      #openInfo {
        bottom: 10px;
        display: block;
      }

      #bottomTab {
        #userLogin {
          .userName {
            display: flex; // Show username when nav is enlarged
          }
        }
      }
    }
  }

  @media (max-width: 600px) {
    #app {
      left: 0;
      width: 100%;
    }
  }
}

// ISV Client Selector - moved outside nav to prevent clipping
#selectISV {
  position: fixed;
  bottom: 80px; // Position above the ISV toggle button (15px bottom + 60px height + some margin)
  left: 15px;
  width: 350px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  overflow: hidden;
  z-index: 1500;
  transition: all 0.2s ease-in-out;

  &.navEnlarge {
    left: 15px; // Keep same left margin when enlarged
    width: 370px; // Slightly wider when nav is enlarged
  }

  .client-selector-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(135deg, #04182d 0%, lighten(#04182d, 8%) 100%);
    color: white;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    h4 {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.5px;
    }

    .close-selector {
      background: none;
      border: none;
      color: #ccc;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.2s ease;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
      }
    }
  }

  .client-list {
    max-height: 280px;
    overflow-y: auto;
    padding: 8px 0;

    .client-item {
      display: flex;
      align-items: center;
      padding: 12px;
      cursor: pointer;
      transition: all 0.2s ease;
      border-left: 3px solid transparent;

      &:hover {
        background: #f5f7fb;
        border-left-color: lighten($colourP, 20%);
      }

      &.active {
        background: linear-gradient(90deg, rgba($colourP, 0.1) 0%, transparent 100%);
        border-left-color: $colourP;

        .client-name {
          color: $colourP;
          font-weight: 600;
        }
      }

      .client-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: linear-gradient(135deg, lighten($colourP, 10%) 0%, $colourS 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 600;
        font-size: 14px;
        margin-right: 12px;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba($colourP, 0.2);

        &.all-clients {
          background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
        }
      }

      .client-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        min-width: 0;
        text-align: left;

        .client-name {
          font-size: 14px;
          font-weight: 500;
          color: #1f2937;
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          width: 100%;
        }

        .client-description {
          font-size: 12px;
          color: #6b7280;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          width: 100%;
        }
      }

      .client-status {
        width: 20px;
        display: flex;
        justify-content: center;
        align-items: center;

        i {
          color: $colourP;
          font-size: 14px;
        }
      }
    }
  }

  .client-actions {
    padding: 12px 16px;
    background: #f5f7fb;
    border-top: 1px solid #e5e7eb;

    .add-client-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      padding: 10px 16px;
      background: linear-gradient(135deg, $colourP 0%, $colourS 100%);
      color: white;
      text-decoration: none;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 500;
      transition: all 0.2s ease;
      box-sizing: border-box;
      box-shadow: 0 2px 4px rgba($colourP, 0.2);

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba($colourP, 0.3);
      }

      i {
        margin-right: 8px;
        font-size: 12px;
      }
    }
  }
}

.beta-badge {
  font-size: 0.5em !important;
  background-color: #2562be90;
  opacity: 1 !important;
  color: white !important;
  padding: 1px 5px;
  border-radius: 4px;
  margin-left: 10px !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  display: inline-block !important;
  position: relative;
  top: -1px;
}

#notificationContainer {
  position: relative;

  #notificationBell {
    position: relative;
    font-size: medium;
    border-radius: 40px;
    width: 30px;
    height: 30px;
    color: #85888b;
    transition: all 0.2s;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    &:hover {
      color: white;
      background-color: lighten(#2c3e50, 10%);
    }

    .notification-counter {
      position: absolute;
      top: -4px;
      right: -8px;
      min-width: 18px;
      height: 18px;
      background-color: #ef4444;
      color: white;
      border-radius: 9px;
      font-size: 10px;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0 5px;
      font-weight: bold;
    }
  }
}

.notifications-popup {
  position: fixed;
  bottom: 70px;
  left: 20px;
  width: 450px;
  max-width: 100vw;
  max-height: 400px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  z-index: 1500;
  color: #333;
  text-align: left;
  transition: left 0.2s ease-in-out;

  .notifications-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
    background-color: #f1f1f1;
    border-bottom: 1px solid #e6e6e6;

    button {
      border: none;
      color: $colourP;
      padding: 0;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .notifications-list {
    max-height: 300px;
    overflow-y: auto;
    padding: 10px;

    .notification-item {
      display: flex;
      align-items: center;
      padding: 10px;
      border-radius: 4px;
      transition: background-color 0.2s;

      &:hover {
        background-color: #f9f9f9;
      }

      .notification-icon {
        margin-right: 10px;
      }

      .notification-content {
        flex: 1;

        h4 {
          margin: 0;
          font-size: 1em;
        }

        p {
          margin: 5px 0;
          font-size: 0.9em;
        }

        small {
          font-size: 0.8em;
          color: #999;
        }
      }
    }
  }

  .notifications-empty {
    padding: 10px;
    text-align: center;
    color: #999;
  }
}

#mobile-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  padding: 0 1em;

  h1 {
    font-size: 1.8em;
    margin-bottom: 0.5em;
  }

  p {
    font-size: 1.2em;
    margin-bottom: 1em;
  }
}

.fa-spinner {
  animation: spin 1s linear infinite;
}

// Loader
@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }

  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@media screen and (max-width: 750px) {

  .router-view,
  #app {
    left: 0;
    width: 100% !important;
  }

  .router-view.isv {
    height: calc(100% - 20px);
  }

  nav {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 64px;
    z-index: 2;
    background: white;
    box-shadow: 0 -1px 0 #e5e7eb, 0 -4px 24px rgba(0, 0, 0, 0.06);
    border-top: 1px solid #f1f5f9;

    &.isv {
      height: 100px;

      #flex-nav-items {
        height: 64px;
      }

      #isvToggle {
        padding: 12px 16px;
        background: rgba(255, 255, 255, 0.95);
        border-radius: 12px 12px 0 0;
        box-sizing: border-box;
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: #374151;
        transition: all 0.2s;
        cursor: pointer;
        border-bottom: 1px solid #e5e7eb;

        .mobile-current-client {
          display: flex;
          align-items: center;
          flex: 1;

          .mobile-current-avatar {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: linear-gradient(135deg, $colourP 0%, $colourS 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 600;
            font-size: 14px;
            margin-right: 12px;
          }

          span {
            font-size: 14px;
            font-weight: 500;
            color: #374151;
          }
        }

        i {
          font-size: 12px;
          color: #6b7280;
          transition: transform 0.3s ease;

          &.rotated {
            transform: rotate(180deg);
          }
        }
      }

      #selectISV {
        position: absolute;
        bottom: 50px;
        left: 0;
        right: 0;
        background: white;
        box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
        border-radius: 16px 16px 0 0;
        overflow: hidden;

        .mobile-client-list {
          max-height: 60vh;
          overflow-y: auto;
          padding: 16px 0;

          .mobile-client-item {
            display: flex;
            align-items: center;
            padding: 14px 20px;
            cursor: pointer;
            transition: all 0.2s ease;
            border-left: 4px solid transparent;

            &:hover {
              background: #f5f7fb;
            }

            &.active {
              background: linear-gradient(90deg, rgba($colourP, 0.1) 0%, transparent 100%);
              border-left-color: $colourP;

              .mobile-client-name {
                color: $colourP;
                font-weight: 600;
              }
            }

            .mobile-client-avatar {
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background: linear-gradient(135deg, lighten($colourP, 10%) 0%, $colourS 100%);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: 600;
              font-size: 16px;
              margin-right: 16px;
              flex-shrink: 0;

              &.all-clients {
                background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
              }
            }

            .mobile-client-name {
              flex: 1;
              font-size: 16px;
              font-weight: 500;
              color: #374151;
            }

            .mobile-check {
              color: $colourP;
              font-size: 16px;
            }
          }

          .mobile-add-client {
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 16px 20px 0;
            padding: 14px 20px;
            background: linear-gradient(135deg, $colourP 0%, $colourS 100%);
            color: white;
            text-decoration: none;
            border-radius: 12px;
            font-size: 15px;
            font-weight: 500;
            transition: all 0.2s ease;

            &:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba($colourP, 0.3);
            }

            i {
              margin-right: 10px;
              font-size: 14px;
            }
          }
        }
      }
    }

    #flex-nav-items {
      width: 100%;
      display: flex;
      justify-content: space-around;
      align-items: stretch;
      height: 64px;
      padding-bottom: env(safe-area-inset-bottom, 0);

      a {
        color: #9ca3af;
        text-decoration: none;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 3px;
        flex: 1;
        height: 100%;
        border: none;
        border-radius: 0;
        margin: 0;
        padding: 0;
        transition: color 0.2s ease;
        background: transparent;
        position: relative;

        i {
          font-size: 19px;
          transition: transform 0.2s ease;
        }

        span {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.2px;
        }

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 20%;
          right: 20%;
          height: 2px;
          background: $colourP;
          border-radius: 0 0 3px 3px;
          transform: scaleX(0);
          transition: transform 0.2s ease;
        }

        &.router-link-active {
          color: $colourP;

          i {
            transform: translateY(-1px);
          }

          &::before {
            transform: scaleX(1);
          }
        }
      }
    }
  }
}

/* Authentication Loading Overlay */
.auth-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  display: none;
  /* Hidden by default */
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  /* Show when needed */
  &.show {
    display: flex;
  }

  .loading-content {
    text-align: center;
    padding: 40px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(0, 0, 0, 0.1);

    .spinner {
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #2596be;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px;
    }

    p {
      margin: 0;
      color: #333;
      font-size: 16px;
      font-weight: 500;
    }
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Help Modal */
.help-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-in-out;
}

.help-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 900px;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
}

.help-modal-content {
  display: flex;
  flex: 1;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.help-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px;
  overflow: hidden;

  h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }

  .help-description {
    margin: 0 0 16px 0;
    font-size: 14px;
    color: #6b7280;
  }

  .ai-helper-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
  }
}

.help-divider {
  width: 1px;
  background: linear-gradient(180deg, transparent 0%, #e5e7eb 10%, #e5e7eb 90%, transparent 100%);

  @media (max-width: 768px) {
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, #e5e7eb 10%, #e5e7eb 90%, transparent 100%);
  }
}

.help-center-section {
  flex: 0 0 300px;
  justify-content: center;
  align-items: center;
  text-align: center;
  background: linear-gradient(135deg, #f5f7fb 0%, #f1f5f9 100%);

  @media (max-width: 768px) {
    flex: 0 0 auto;
  }

  .close-modal-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    font-size: 18px;
    position: absolute;
    top: 16px;
    right: 16px;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: rotate(90deg);
    }
  }

  .help-center-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;

    i {
      font-size: 48px;
      color: #2596be;
      opacity: 0.8;
    }

    div {
      h3 {
        margin-bottom: 4px;
      }

      p {
        margin: 0;
        font-size: 14px;
        color: #6b7280;
      }
    }
  }

  .help-center-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: linear-gradient(135deg, #2596be 0%, #2562be 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(37, 150, 190, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(37, 150, 190, 0.4);
    }

    i {
      font-size: 12px;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
